import React, { useEffect, useMemo, useRef } from 'react';
import { navigate } from 'gatsby';
import { useDispatch, useSelector } from 'react-redux';
import {
  CompareProvider,
  CompareTable,
  CompareItem,
  CompareConfiguration
} from '@common-modules/product-catalogue';
import { Grid, Row, Column, Spacing, Text, TextLink } from '@dls/web';
import { getUrlParams } from '@wec-core/helpers';
import { setUrlParams } from '@lux/helpers';
import { KeyValue } from '../../types/common.types';
import { Plan, PlanGroup } from '../../types/plan.types';
import { flattenNodes } from '../../helpers/common';
import {
  filterProduct,
  formatProducts,
  getSimOnlyPlans
} from '../../helpers/productCatalogHelper';
import {
  getCompareCatalog,
  getCompareCtaHref,
  getCompareItemSlug,
  getCompareSchema,
  getDefaultPlanValue,
  hasPlanOption
} from '../../helpers/compare-helpers';
import {
  COMPARE_MAX_ITEMS,
  COMPARE_MIN_ITEMS
} from '../../constants/compare-specs';
import { setCompareItems } from '../../reducers/productCompare';
import { trans as t } from '../../helpers/localisation';
import CONSTANTS from '../../constants/common';
import DevicePicker from './DevicePicker';

type PropTypes = {
  data: KeyValue;
  isCisPage?: boolean;
};

export const PhoneCompare = (props: PropTypes) => {
  const { data, isCisPage } = props;
  const dispatch = useDispatch();

  const {
    selectedPlan,
    compareItems,
    sharedCatalog,
    sharedSchema
  } = useSelector((state: KeyValue) => ({
    selectedPlan: state.plan?.selectedPlan as Plan,
    compareItems: (state.productCompare?.items || []) as CompareItem[],
    sharedCatalog: (state.productCompare?.catalog || []) as CompareItem[],
    sharedSchema: state.productCompare?.schema || []
  }));

  const anchorTagPath = `${process.env.GATSBY_HOST}/${CONSTANTS.URL_PATHS.PHONE}`;
  const catalogueUrl = isCisPage ? '/cis-phones' : '/';

  // Falls back to the page data only on a cold load, when the catalogue page never ran.
  const formattedProducts = useMemo(() => {
    const plans = flattenNodes(data?.allPlan) as Plan[];
    const products = flattenNodes(data?.allPhone);
    const formatted = formatProducts({
      products,
      selectedPlan,
      simOnlyPlans: getSimOnlyPlans(plans)
    });
    // Mirror the catalogue page's always-on eligibility rules (no ROI/campaign SKUs,
    // must have a valid price for the current plan) — filterData is empty on a cold
    // load since there is no UI filter state to replicate here.
    return filterProduct({ products: formatted, filterData: [], selectedPlan });
  }, [data, selectedPlan]);

  const catalog = useMemo(() => {
    if (sharedCatalog.length) {
      return sharedCatalog;
    }
    const plans = flattenNodes(data?.allPlan) as Plan[];
    const planGroups = flattenNodes(data?.allPlanGroup) as PlanGroup[];
    return getCompareCatalog(formattedProducts, {
      anchorTagPath,
      plans,
      planGroups
    });
  }, [sharedCatalog, formattedProducts, data, anchorTagPath]);

  const schema = useMemo(
    () =>
      sharedSchema.length ? sharedSchema : getCompareSchema(formattedProducts),
    [sharedSchema, formattedProducts]
  );

  // Remembers each slot's chosen plan by position (not device id) so a device swap keeps the plan.
  const slotPlansRef = useRef<Record<number, string>>({});

  // Storage defaults to the cheapest option; plan carries over on a device swap when still offered, else its first plan.
  const defaultConfiguration = useMemo(
    () =>
      compareItems.reduce(
        (acc: Record<string, CompareConfiguration>, item, index) => {
          const storage = item.storageOptions?.[0]?.value;
          const carriedPlan = slotPlansRef.current[index];
          const plan = hasPlanOption(item, carriedPlan)
            ? carriedPlan
            : getDefaultPlanValue(item);
          const config: CompareConfiguration = {
            ...(storage && { storage }),
            ...(plan && { plan })
          };
          if (Object.keys(config).length) {
            acc[`${item.id}-${index}`] = config;
          }
          return acc;
        },
        {}
      ),
    [compareItems]
  );

  // Mirror the live dropdown state into the per-slot memory that survives a device swap.
  const rememberSlotPlans = (
    configuration: Record<string, CompareConfiguration>
  ) => {
    slotPlansRef.current = Object.entries(configuration).reduce(
      (acc: Record<number, string>, [key, value]) => {
        if (value.plan) {
          acc[Number(key.slice(key.lastIndexOf('-') + 1))] = value.plan;
        }
        return acc;
      },
      {}
    );
  };

  // Direct/bare visits without a `devices` param have nothing to compare — bounce back to this segment's catalogue.
  useEffect(() => {
    const devices = (getUrlParams(window.location.search) as KeyValue)?.devices;
    if (!devices) {
      navigate(catalogueUrl);
    }
  }, []);

  // Rehydrate the selection from the URL so the page survives a reload or a shared link.
  const hasRestoredRef = useRef(false);
  useEffect(() => {
    if (hasRestoredRef.current || !catalog.length) {
      return;
    }
    hasRestoredRef.current = true;
    if (compareItems.length) {
      return;
    }
    const slugs = String(
      (getUrlParams(window.location.search) as KeyValue)?.devices || ''
    )
      .split(',')
      .filter(Boolean);
    const restored = slugs
      .map(slug => catalog.find(item => getCompareItemSlug(item) === slug))
      .filter(Boolean) as CompareItem[];
    if (restored.length) {
      dispatch(setCompareItems(restored));
    }
  }, [catalog]);

  // Keep the URL in sync so adding/changing/removing a device is shareable and survives a reload.
  useEffect(() => {
    if (!hasRestoredRef.current) {
      return;
    }
    const search = setUrlParams('', {
      ...((getUrlParams(window.location.search) || {}) as KeyValue),
      devices: compareItems.map(getCompareItemSlug).join(',')
    });
    window.history.replaceState(
      window.history.state,
      '',
      `${window.location.pathname}${search}`
    );
  }, [compareItems]);

  const goBackToCatalogue = () => {
    const { devices, ...queryParams } =
      (getUrlParams(window.location.search) as KeyValue) || {};
    navigate(setUrlParams(catalogueUrl, queryParams));
  };

  return (
    <CompareProvider
      value={compareItems}
      onChange={items => dispatch(setCompareItems(items))}
      catalog={catalog}
      schema={schema}
      max={COMPARE_MAX_ITEMS}
      min={COMPARE_MIN_ITEMS}
      onRequestAddItem={goBackToCatalogue}
      renderAddPicker={({
        candidates,
        add,
        close
      }: {
        candidates: CompareItem[];
        add: (item: CompareItem) => void;
        close: () => void;
      }) => <DevicePicker candidates={candidates} select={add} close={close} />}
    >
      <Grid>
        <Row>
          <Column>
            <Spacing topBottom={3}>
              <Text type="pageTitle">{t('COMPARE_PAGE_TITLE')}</Text>
            </Spacing>
            <CompareTable
              defaultConfiguration={defaultConfiguration}
              onConfigurationChange={rememberSlotPlans}
              getCtaHref={getCompareCtaHref}
            />
          </Column>
        </Row>
      </Grid>
    </CompareProvider>
  );
};

export default PhoneCompare;
